import { useCallback, useRef, useState } from 'react';

import type { SoftphoneStatus } from './softphoneClient.types';
import type { VoicemailDropControl, VoicemailDropState } from './VoicemailDropButton';

/**
 * The call a `voiceCall.startCall` response allows a voicemail drop on, or null. `voicemailDrop` is
 * optional on purpose: an extension build can meet a server deployed before the field existed, and
 * a missing field must read as "not allowed", never crash the dial.
 */
export function voicemailDropAllowedCallId(res: {
  voiceCallId: string;
  voicemailDrop?: { allowed: boolean } | null;
}): string | null {
  return res.voicemailDrop?.allowed === true ? res.voiceCallId : null;
}

/**
 * The voicemail-drop state machine every dialer surface shares (dashboard floating dialer, the
 * `/todo/calls` power dialer, the extension) — platform-agnostic: the surface injects the network
 * call (`drop`) and what "move on" means (`onDropped`).
 *
 * Offered only while the call is answered (`active` — the rep has to hear the beep) and only on the
 * call the server allowed it for (`allowedVoiceCallId`, from `voiceCall.startCall`). A second click
 * while the first is in flight is ignored here, and the server's claim makes a racing duplicate a
 * no-op too. A failure returns the button to clickable with an inline error — the call is still live.
 */
export function useVoicemailDrop(params: {
  voiceCallId: string | null;
  status: SoftphoneStatus;
  /** The call the server said a drop is allowed on, or null. */
  allowedVoiceCallId: string | null;
  /** Ask the server to drop the voicemail on this call. Rejects on failure. */
  drop: (voiceCallId: string) => Promise<unknown>;
  /** After a successful drop: release the browser leg and move on (log Left voicemail, advance). */
  onDropped: (voiceCallId: string) => void;
}): VoicemailDropControl | undefined {
  const { voiceCallId, status, allowedVoiceCallId, drop, onDropped } = params;
  // Keyed by call so a new call starts clean without an effect.
  const [dropState, setDropState] = useState<{ callId: string; state: VoicemailDropState } | null>(null);
  const inFlightRef = useRef<string | null>(null);

  const onDrop = useCallback(() => {
    if (voiceCallId == null || inFlightRef.current === voiceCallId) return;
    inFlightRef.current = voiceCallId;
    setDropState({ callId: voiceCallId, state: 'dropping' });
    drop(voiceCallId).then(
      () => {
        inFlightRef.current = null;
        setDropState({ callId: voiceCallId, state: 'idle' });
        onDropped(voiceCallId);
      },
      () => {
        inFlightRef.current = null;
        setDropState({ callId: voiceCallId, state: 'error' });
      },
    );
  }, [voiceCallId, drop, onDropped]);

  if (voiceCallId == null || voiceCallId !== allowedVoiceCallId || status !== 'active') return undefined;
  const state = dropState?.callId === voiceCallId ? dropState.state : 'idle';
  return { state, onDrop };
}
